import { useCallback, useEffect, useRef, useState } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { ChevronLeftIcon, DeleteIcon, Eye, EyeClosed, PlusCircle, Save, Trash, TriangleAlert, X } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer, Tooltip } from "antd";
import { EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, SALARY_COMPONENT, SALARY_TEMPLATE } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";

export const default_total_earnings = {
    percentage: 0,
    monthly_amount: 0,
    annual_amount: 0,
}

export const default_total_deduction = {
    percentage: 0, // dedutions percentage of employee
    employee_monthly_amount: 0,
    employer_monthly_amount: 0,
    annual_amount: 0
}

// default states
let initialFormData = {
    id: '',
    is_active: true,
    code: "",
    salary_template_name: "",
    salary_department: "",
    salary_designation: "",
    monthly_ctc: 0,
    annual_ctc: 0,
    earning_components: [],
    total_earning: { ...default_total_earnings },
    deduction_components: [],
    total_deduction: { ...default_total_deduction },
    net_salary: 0,

    // add new components
    component_type: "",
    components: [],
    is_edit: false,
    is_draft: false,
    edit_component: []
}

let initalDropdownList = {
    designationList: [],
    departmentlist: [],
}

const CustomDropdown = ({
    label = "Select",
    list = [],
    displayKey = "name",
    selectedItem,
    setSelectedItem,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [hoveringIcon, setHoveringIcon] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    const filteredList = list.filter((item) =>
        item[displayKey].toLowerCase().includes(search.toLowerCase())
    );

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredList.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredList.length - 1
            );
        } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            setSelectedItem(filteredList[highlightedIndex]);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    // Scroll selected into view
    useEffect(() => {
        if (listRef.current && highlightedIndex !== -1) {
            const el = listRef.current.children[highlightedIndex];
            if (el) el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    return (
        <div ref={dropdownRef} className="relative w-full">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>

            {/* Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 shadow-sm cursor-pointer flex justify-between items-center focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
            >
                <span className={`text-xs ${selectedItem ? "text-black dark:text-white" : "text-gray-400 dark:text-gray-500"}`}>
                    {selectedItem ? selectedItem[displayKey] : `Select ${label.toLowerCase()}`}
                </span>

                {/* Hover-based icon change */}
                <div
                    onMouseEnter={() => setHoveringIcon(true)}
                    onMouseLeave={() => setHoveringIcon(false)}
                    onClick={(e) => {
                        if (hoveringIcon && selectedItem) {
                            e.stopPropagation();
                            setSelectedItem(null);
                            setSearch("");
                            setIsOpen(false);
                        }
                    }}
                    className="ml-2 transition-colors duration-200"
                >
                    {hoveringIcon && selectedItem ? (
                        <Trash className="w-3 h-3 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300" />
                    ) : (
                        <svg
                            className={`w-4 h-4 transform transition-transform duration-200 text-gray-500 dark:text-gray-400 ${isOpen ? "rotate-180" : "rotate-0"}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    )}
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50">
                    {/* Search input */}
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setHighlightedIndex(0);
                        }}
                        placeholder="Search..."
                        className="w-full px-3 py-2 border-b dark:border-gray-600 rounded-t-md border-gray-200 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <ul ref={listRef} className="max-h-60 overflow-y-auto">
                        {filteredList.length === 0 ? (
                            <li className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-700">
                                No options found
                            </li>
                        ) : (
                            filteredList.map((item, index) => (
                                <li
                                    key={index}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setIsOpen(false);
                                        setSearch("");
                                        setHighlightedIndex(-1);
                                    }}
                                    className={`px-4 py-2 text-xs cursor-pointer transition-all duration-150
                                ${index === highlightedIndex
                                            ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                            : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        }`}
                                >
                                    {item[displayKey]}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const ComponentTable = ({ components, setComponents, type = "Earnings", monthlyGross, componentList = [], earningComponents }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [newComponent, setNewComponent] = useState({
        name: "",
        percentage: "",
        calcof: "CTC",
        is_amount: null,
        is_employee_share: null,
        is_employer_share: null,
        employee_share_percentage: type === "Deductions" ? 0 : undefined,
        employer_share_percentage: type === "Deductions" ? 0 : undefined
    });
    const [visibleAmounts, setVisibleAmounts] = useState({});
    const customConfigLocal = JSON.parse(sessionStorage.getItem("customConfig"))
    const pfLimit = customConfigLocal?.pf_limit || 15000;
    const capAmount = customConfigLocal?.cap_amount || 0;
    const ESCILimitUpto = customConfigLocal?.esic_limit_upto || 21000;

    // console.log(pfLimit, capAmount, ESCILimitUpto); // Debugging purpose: 15000, 1800, 21000

    const toggleVisibility = (index) => {
        setVisibleAmounts((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };


    const handleChange = (index, key, value) => {
        const updated = [...components];
        updated[index][key] = value;
        setComponents(updated);
    };

    const handleDelete = (index) => {
        const updated = components.filter((_, i) => i !== index);
        setComponents(updated);
    };

    const handleAdd = () => {
        setIsDrawerOpen(true);
    };

    // Calculate amounts based on percentage and monthly CTC
    const calculateAmounts = (component) => {
        if (type === "Earnings") {
            const percentage = parseFloat(component.percentage) || 0;

            let baseAmount = 0;

            if (component.calcof === "CTC") {
                baseAmount = monthlyGross;
            } else {
                // find the reference earning component
                const refComponent = earningComponents.find(c => c.name === component.calcof);
                if (refComponent) {
                    // if refComponent already has monthly_amount calculated, use that
                    baseAmount = parseFloat(refComponent.monthly_amount) || 0;

                    // fallback → recalc from percentage if not yet calculated
                    if (!baseAmount) {
                        const refPercentage = parseFloat(refComponent.percentage) || 0;
                        baseAmount = (monthlyGross * refPercentage) / 100;
                    }
                }
            }

            const monthly = (baseAmount * percentage) / 100;

            return {
                monthly: monthly.toFixed(2),
                annual: (monthly * 12).toFixed(2)
            };
        } else {
            // Deductions
            const basicPercentage = parseFloat(
                earningComponents.find(c => c.name === "Basic")?.percentage
            ) || 0;
            const DAPercentage = parseFloat(
                earningComponents.find(c => c.name === "DA")?.percentage
            ) || 0;
            const basicAmount = (monthlyGross * basicPercentage) / 100;
            const DAAmount = (monthlyGross * DAPercentage) / 100;

            const baseAmount = component.calcof === "Basic" ? basicAmount + DAAmount : monthlyGross;

            const empPercent = parseFloat(component.employee_share_percentage) || 0;
            const employerPercent = parseFloat(component.employer_share_percentage) || 0;

            let employeeMonthly = 0;
            let employerMonthly = 0;

            // PF limit handling
            if (component.name.toLowerCase() === "pf") {
                // PF amount is based on basic amount but capped
                const pfBase = Math.min(baseAmount, pfLimit);
                employeeMonthly = component.is_employee_share ? (pfBase * empPercent) / 100 : 0;
                employerMonthly = component.is_employer_share ? (pfBase * employerPercent) / 100 : 0;

                // If capped monthly amount is set, override
                if (capAmount > 0 && pfBase === pfLimit) {
                    employeeMonthly = component.is_employee_share ? capAmount : 0;
                    employerMonthly = component.is_employer_share ? capAmount : 0;
                }

                // ESIC limit handling
            } else if (component.name.toLowerCase() === "esic") {
                if (monthlyGross <= ESCILimitUpto) {
                    employeeMonthly = component.is_employee_share ? (baseAmount * empPercent) / 100 : 0;
                    employerMonthly = component.is_employer_share ? (baseAmount * employerPercent) / 100 : 0;
                } else {
                    // Above limit → ESIC is zero
                    employeeMonthly = 0;
                    employerMonthly = 0;
                }

                // Default deductions
            } else {
                employeeMonthly = component.is_employee_share ? (baseAmount * empPercent) / 100 : 0;
                employerMonthly = component.is_employer_share ? (baseAmount * employerPercent) / 100 : 0;
            }

            let totalMonthly;
            if (!component.is_employee_share && !component.is_employer_share) {
                totalMonthly = parseFloat(component.monthly_amount) || 0;
            } else {
                totalMonthly = employeeMonthly + employerMonthly;
            }

            return {
                monthly: totalMonthly.toFixed(2),
                annual: (totalMonthly * 12).toFixed(2),
                employeeMonthly: employeeMonthly.toFixed(2),
                employerMonthly: employerMonthly.toFixed(2)
            };
        }
    };

    // Calculate totals for earnings
    const totalEarningsPercentage = components?.reduce((acc, curr) => {
        return curr.calcof === "CTC" && type === "Earnings" ? acc + Number(curr.percentage || 0) : acc;
    }, 0);
    const totalEarningMonthly = components?.reduce((acc, curr) => {
        if (curr.calcof === "CTC") {
            return acc + (monthlyGross * (curr.percentage || 0)) / 100;
        }
        return acc;
    }, 0);
    const totalEarningAnnual = totalEarningMonthly * 12;

    // Calculate totals for deductions
    const totalEmployeeDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employee_share_percentage || 0) : acc;
    }, 0);
    const totalEmployerDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employer_share_percentage || 0) : acc;
    }, 0);
    const totalDeductionMonthly = type === "Deductions"
        ? components.reduce((acc, component) => acc + Number(calculateAmounts(component).monthly), 0)
        : 0;

    const totalDeductionAnnual = totalDeductionMonthly * 12;

    // Handle adding a new component
    const handleAddComponent = () => {
        if (!newComponent.name) return;

        const componentToAdd = {
            id: newComponent.id,
            name: newComponent.name,
            calcof: newComponent.calcof,
            ...(type === "Earnings" ? {
                percentage: parseFloat(newComponent.percentage),
                annual_amount: 0,
                monthly_amount: 0
            } : {
                is_amount: newComponent.is_amount,
                is_employee_share: newComponent.is_employee_share,
                is_employer_share: newComponent.is_employer_share,
                employee_share_percentage: parseFloat(newComponent.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(newComponent.employer_share_percentage) || 0,
                employee_share_monthly_amount: 0,
                employer_share_monthly_amount: 0,
                percentage: parseFloat(newComponent.employee_share_percentage) +
                    parseFloat(newComponent.employer_share_percentage) || 0
            })
        };

        setComponents(prev => [...prev, componentToAdd]);
        setIsDrawerOpen(false);
        setNewComponent({
            name: "",
            percentage: "",
            calcof: "CTC",
            is_amount: type === "Deductions",
            is_employee_share: type === "Deductions",
            is_employer_share: type === "Deductions",
            employee_share_percentage: type === "Deductions" ? 0 : undefined,
            employer_share_percentage: type === "Deductions" ? 0 : undefined
        });
    };

    const hasBasicComponent = earningComponents.some(item => item.name?.toLowerCase() === "basic");
    const hasDAComponent = earningComponents.some(item => item.name?.toLowerCase() === "da");
    const isTotalCTCPercentValid = totalEarningsPercentage?.toFixed(2) === "100.00";

    return (
        <div className="dark:bg-gray-800">
            <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-sidebar-primary dark:bg-blue-500 rounded-full"></div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white">{type}</h3>
            </div>
            <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">Define the {type.toLowerCase()} components of the salary.</p>

            {type == 'Earnings' && (!hasBasicComponent || !isTotalCTCPercentValid) && (
                <div className="py-2 text-red-400 dark:text-red-300 text-[10px]">
                    {!hasBasicComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">Basic</strong> earning component must exist.</div>
                    )}
                    {!isTotalCTCPercentValid && (
                        <div>⚠️ Total earnings percentage calculated from <strong className="dark:text-red-200">CTC</strong> must be exactly <strong className="dark:text-red-200">100%</strong>.</div>
                    )}
                </div>
            )}

            {type === 'Deductions' && (
                <div className="py-2 text-red-400 dark:text-red-300 text-[10px]">
                    {!hasBasicComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">Basic</strong> earning component must exist.</div>
                    )}
                    {!hasDAComponent && (
                        <div>⚠️ A <strong className="dark:text-red-200">DA</strong> deduction component must exist.</div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-12 text-xs font-medium text-gray-600 dark:text-gray-400 border-b dark:border-gray-600 mt-1 pb-2">
                <div className="col-span-2">Component Name</div>
                <div className="col-span-1">Of</div>
                <div className="col-span-2">Percentage</div>
                <div className="col-span-3 text-right">Monthly Amount</div>
                <div className="col-span-3 text-right">Annual Amount</div>
                <div className="col-span-1 text-center"> </div>
            </div>

            {components?.map((item, index) => {
                const amounts = calculateAmounts(item);

                return (
                    <div
                        key={index}
                        className={
                            `grid grid-cols-12 gap-2 text-xs items-center py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-600 last:border-b-0 transition-colors duration-150
                    ${item.calcof === "Basic" && type !== "Deductions" ? "bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white" : ""}`
                        }
                    >
                        <div className="col-span-2">
                            <span className='text-gray-700 dark:text-gray-300'>{item.name}</span>
                        </div>
                        <div className="col-span-1">
                            <span className={`inline-block px-2 py-1 rounded-full font-medium text-[11px] 
                    ${item.calcof === "CTC" ? "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300" :
                                    item.calcof === "Basic" ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" :
                                        "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300"}`}>
                                {item.calcof}
                            </span>
                        </div>

                        {type === "Deductions" ? (
                            <>
                                {
                                    !item.is_employee_share && !item.is_employer_share ? (
                                        <div className='col-span-2 text-gray-600 dark:text-gray-400'>Fixed Amount</div>
                                    ) : (
                                        <>
                                            <div className="col-span-1">
                                                <Tooltip title="Employee Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employee_share_percentage}
                                                        onChange={(e) => {
                                                            let val = Number(e.target.value);
                                                            if (val > 100) val = 100;
                                                            handleChange(index, "employee_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employee_share}
                                                        className={`w-full px-2 py-1 rounded-md border text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 ${!item.is_employee_share
                                                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed text-gray-500 dark:text-gray-500'
                                                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400'
                                                            } transition-colors duration-200`}
                                                    />
                                                </Tooltip>
                                            </div>
                                            <div className="col-span-1">
                                                <Tooltip title="Employer Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employer_share_percentage}
                                                        onChange={(e) => {
                                                            let val = Number(e.target.value);
                                                            if (val > 100) val = 100;
                                                            handleChange(index, "employer_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employer_share}
                                                        className={`w-full px-2 py-1 rounded-md border text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 ${!item.is_employer_share
                                                            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed text-gray-500 dark:text-gray-500'
                                                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400'
                                                            } transition-colors duration-200`}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </>
                                    )
                                }
                            </>
                        ) : (
                            <div className="col-span-2">
                                <input
                                    type="number"
                                    placeholder="Enter %"
                                    value={item.percentage}
                                    onChange={(e) => handleChange(index, "percentage", e.target.value)}
                                    className="w-full px-2 py-1 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
                                />
                            </div>
                        )}

                        <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-medium flex items-center justify-end gap-2">
                            {(type === 'Deductions' && !item.is_employee_share && !item.is_employer_share) ? (
                                <input
                                    type="number"
                                    placeholder="₹ Enter monthly amount"
                                    value={item.monthly_amount || ""}
                                    onChange={(e) => handleChange(index, "monthly_amount", e.target.value)}
                                    className="w-[100px] px-2 py-1 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-right focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
                                />
                            ) : (
                                <span>
                                    {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                        ? `₹${amounts.employeeMonthly} / ₹${amounts.employerMonthly}`
                                        : `₹${amounts.monthly}`
                                    }
                                </span>
                            )}

                            {item.is_employee_share && item.is_employer_share && (
                                <button
                                    onClick={() => toggleVisibility(index)}
                                    className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                                >
                                    {visibleAmounts[index] ? (
                                        <Eye className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    ) : (
                                        <EyeClosed className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="col-span-3 text-right text-gray-700 dark:text-gray-300 font-medium flex items-center justify-end gap-2">
                            <span>
                                {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                    ? `₹${amounts.employeeMonthly * 12} / ₹${amounts.employerMonthly * 12}`
                                    : `₹${amounts.annual}`
                                }
                            </span>
                            {item.is_employee_share && item.is_employer_share && (
                                <button
                                    onClick={() => toggleVisibility(index)}
                                    className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                                >
                                    {visibleAmounts[index] ? (
                                        <Eye className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    ) : (
                                        <EyeClosed className="text-gray-500 dark:text-gray-400 w-4 h-4 hover:text-gray-700 dark:hover:text-gray-200" />
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="col-span-1 flex justify-center">
                            <button
                                onClick={() => handleDelete(index)}
                                className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-1 transition-colors duration-200"
                            >
                                <DeleteIcon className="w-4 h-4 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300" />
                            </button>
                        </div>
                    </div>
                );
            })}

            <button
                onClick={handleAdd}
                className="mt-3 flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
            >
                <PlusCircle className="w-4 h-4" />
                Add {type.slice(0, -1)}
            </button>

            {/* drawer */}
            <Drawer
                title={`Add ${type.slice(0, -1)} Component`}
                placement="right"
                width={400}
                onClose={() => setIsDrawerOpen(false)}
                open={isDrawerOpen}
                className="dark:bg-gray-800 dark:text-white"
                styles={{
                    body: {
                        backgroundColor: 'var(--antd-drawer-bg)',
                        color: 'var(--antd-text)'
                    },
                    header: {
                        backgroundColor: 'var(--antd-drawer-bg)',
                        color: 'var(--antd-text)',
                        borderBottom: '1px solid var(--antd-border-color)'
                    }
                }}
            >
                <div className="flex flex-col gap-4 dark:bg-gray-800">
                    <CustomDropdown
                        label="Component Name"
                        list={componentList}
                        displayKey="salary_component_name"
                        selectedItem={newComponent.name ?
                            { salary_component_name: newComponent.name } :
                            null
                        }
                        setSelectedItem={(value) =>
                            setNewComponent({
                                ...newComponent,
                                id: value.id,
                                name: value.salary_component_name,
                                is_amount: value.amount,
                                is_employee_share: value.employee_share,
                                is_employer_share: value.employer_share
                            })
                        }
                    />

                    <CustomDropdown
                        label="Calculate Of"
                        list={[{ name: "CTC" }, { name: "Basic" }]}
                        selectedItem={{ name: newComponent.calcof }}
                        setSelectedItem={(val) => setNewComponent({ ...newComponent, calcof: val.name })}
                    />

                    {type === "Earnings" && (
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Percentage
                            </label>
                            <input
                                type="number"
                                placeholder='Enter percentage'
                                value={newComponent.percentage}
                                onChange={(e) => setNewComponent({ ...newComponent, percentage: e.target.value })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
                            />
                        </div>
                    )}

                    <button
                        onClick={handleAddComponent}
                        className="bg-[#04B7B1] hover:bg-[#04B7B1]/90 dark:bg-blue-600 dark:hover:bg-blue-700 text-white py-2 px-4 rounded mt-4 transition-colors duration-200 font-medium"
                    >
                        Add Component
                    </button>
                </div>
            </Drawer>

            <div className="mt-4 border-t dark:border-gray-600 pt-3 flex justify-end text-xs text-gray-700 dark:text-gray-300 font-medium">
                <div className="space-x-6 flex items-center">
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total % (of CTC):{" "}
                                <span
                                    className={`font-semibold ${type === 'Earnings' && totalEarningsPercentage?.toFixed(2) !== "100.00"
                                        ? "text-red-600 dark:text-red-400"
                                        : "text-gray-700 dark:text-gray-300"
                                        }`}
                                >
                                    {totalEarningsPercentage?.toFixed(2)}%
                                </span>
                            </span>
                        ) : (
                            <span className='flex gap-3'>
                                <span>
                                    Total (Employee) %:{" "}
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        {totalEmployeeDeductionsPercentage?.toFixed(2)}%
                                    </span>
                                </span>
                                <span>
                                    Total (Employer) %:{" "}
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        {totalEmployerDeductionsPercentage?.toFixed(2)}%
                                    </span>
                                </span>
                            </span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total Monthly: <strong className="text-gray-900 dark:text-white">₹{totalEarningMonthly?.toFixed(2)}</strong>
                            </span>
                        ) : (
                            <span>
                                Total Monthly: <strong className="text-gray-900 dark:text-white">₹{totalDeductionMonthly?.toFixed(2)}</strong>
                            </span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total Annual: <strong className="text-gray-900 dark:text-white">₹{totalEarningAnnual?.toFixed(2)}</strong>
                            </span>
                        ) : (
                            <span>
                                Total Annual: <strong className="text-gray-900 dark:text-white">₹{totalDeductionAnnual?.toFixed(2)}</strong>
                            </span>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

const SalaryTemplateAddEdit = () => {

    //  states
    const [formData, setFormData] = useState(initialFormData);
    const [dropdownList, setDropDownList] = useState(initalDropdownList);

    const [loadings, setLoadings] = useState(false);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [earningComponents, setEarningComponents] = useState([]);
    const [deductionComponents, setDeductionComponents] = useState([]);

    const [monthlyGross, setMonthlyGross] = useState();
    const [annualGross, setAnnualGross] = useState();

    const [basicPay, setBasicPay] = useState(0);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [totalDeductions, setTotalDeductions] = useState(0);
    const [totalEmployerDeductions, setTotalEmployerDeductions] = useState(0);

    const [netPayMonthly, setNetPayMonthly] = useState(0);
    const [netPayAnnual, setNetPayAnnual] = useState(0);

    const [isFormValid, setIsFormValid] = useState(false);
    const [isCancelClk, setIsCancelClk] = useState(false);

    // variables
    const navigate = useNavigate();
    const location = useLocation();
    const prevPropsRef = useRef();
    const { fetch, update, create } = useApiMethods()
    const { fromEdit, editDetails, isActive } = location.state || {};

    const formValid = () => {
        const { salary_template_name } = formData;

        // console.log("Checking form validity...", formData);

        const validEarnings = earningComponents.some(comp => comp.name?.trim(parseFloat(comp.percentage) > 0))
        const totalEarningsPercentage = earningComponents?.reduce((acc, curr) => {
            return curr.calcof === "CTC" ? acc + Number(curr.percentage || 0) : acc;
        }, 0);
        const hasBasicComponent = earningComponents.some(item => item.name?.toLowerCase() === "basic"); // true if basic exist
        const hasDAComponent = earningComponents.some(item => item.name?.toLowerCase() === "da");

        const isTotalCTCPercentValid = totalEarningsPercentage?.toFixed(2) === "100.00"; // true is total earnings is not equal to 100

        const hasPFInDeductions = deductionComponents?.some(item => item.name?.toLowerCase() === "pf");

        if (!selectedDept) return true;
        if (!selectedDesig) return true;
        if (!validEarnings) return true
        if (!hasBasicComponent) return true;
        if (!isTotalCTCPercentValid) return true;
        if (!salary_template_name.trim()) return true;
        if (hasPFInDeductions && (!hasBasicComponent || !hasDAComponent)) return true;

        return false;
    };

    useEffect(() => {
        setIsFormValid(formValid());
    }, [formData.salary_template_name, formData.salary_department, formData.earning_components]);

    useEffect(() => {
        if (editDetails !== null &&
            Object.keys(editDetails).length !== 0 &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails) &&
            JSON.stringify(formData) !== JSON.stringify(editDetails)) {
            let updateData = {
                is_active: editDetails?.is_active,
                code: editDetails?.code ? editDetails?.code : "",
                salary_template_name: editDetails?.salary_template_name ? editDetails?.salary_template_name : "",
                salary_department: editDetails?.department ? editDetails?.department?.id : "",
                salary_designation: editDetails?.designation ? editDetails?.designation?.id : "",
                monthly_ctc: editDetails?.monthly_ctc ? editDetails?.monthly_ctc : 0,
                annual_ctc: editDetails?.annual_ctc ? editDetails?.annual_ctc : 0,
                earning_components: editDetails?.earning_components ? editDetails?.earning_components : [],
                total_earning: { ...editDetails?.total_earning },
                deduction_components: editDetails?.deduction_components ? editDetails?.deduction_components : [],
                total_deduction: { ...editDetails?.total_deduction },
                net_salary: editDetails?.net_salary ? editDetails?.net_salary : 0,
                is_draft: editDetails?.is_draft ? editDetails?.is_draft : false,
                id: editDetails?.id ? editDetails?.id : 0,
            }

            setSelectedDept(editDetails?.department ? editDetails?.department : "")
            setSelectedDesig(editDetails?.designation ? editDetails?.designation : "")
            setEarningComponents(editDetails?.earning_components ? editDetails?.earning_components : [])
            setDeductionComponents(
                editDetails?.deduction_components?.map(comp => ({
                    ...comp,
                    monthly_amount: comp.is_amount ? comp.employee_share_monthly_amount : 0
                })) || []
            );
            setFormData((prevFormData) => ({
                ...prevFormData,
                id: editDetails.id,
                ...updateData
            }));
            prevPropsRef.current = editDetails;
        }
    }, [editDetails, formData])

    const getEmployeeDesignation = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' }); // Fixed: Changed EMPLOYEE_DEPARTMENT to EMPLOYEE_DESIGNATION
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load designations');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getEmployeeAllDepartment = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load departments');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    const getSalaryComponentList = useCallback(async () => {
        try {
            const apiData = await fetch(SALARY_COMPONENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData; // Return data instead of setting state
            } else {
                ToastMsg('error', 'Failed to load salary components');
                return { status: "error", data: [] };
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return { status: "error", data: [] };
        }
    }, [fetch]);

    // functions
    const fetchDropdownData = async () => {
        setLoadings(true)
        try {
            const [designationRes, departmentRes, salaryComponentRes] = await Promise.all([
                getEmployeeDesignation(),
                getEmployeeAllDepartment(),
                getSalaryComponentList()
            ]);

            const designationList = designationRes?.status === "success" ? designationRes.data : [];
            const departmentlist = departmentRes?.status === "success" ? departmentRes.data : [];

            const allComponents = salaryComponentRes?.status === "success" && Array.isArray(salaryComponentRes?.data)
                ? salaryComponentRes.data
                : [];

            const earningComponents = allComponents.filter(item => item.component_type === "earning");
            const deductionComponents = allComponents.filter(item => item.component_type === "deduction");

            setDropDownList({
                designationList,
                departmentlist,
                componentList: allComponents,
                earningComponents,
                deductionComponents,
            });

            setLoadings(false)

        } catch (error) {
            ToastMsg('error', "Error loadings dropdowns");
            setDropDownList({
                designationList: [],
                departmentlist: [],
                componentList: [],
                earningComponents: [],
                deductionComponents: [],
            });
        }
        setLoadings(false)
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        if (monthlyGross > 0) {
            const breakdown = calculateSalaryBreakdown(monthlyGross);
            setBasicPay(breakdown.basicPay);
            setTotalEarnings(breakdown.totalEarnings);
            setTotalDeductions(breakdown.totalDeductions);
            setTotalEmployerDeductions(breakdown.totalEmployerDeductions);
            setNetPayMonthly(breakdown.netPayMonthly);
            setNetPayAnnual(breakdown.netPayAnnual);
        }
    }, [earningComponents, deductionComponents, monthlyGross]);

    // event handlers
    const preparePayload = (formData, earningComponents, deductionComponents, selectedDept, selectedDesig, isdraft = false) => {
        // Calculate amounts for earning components
        const updatedEarningComponents = earningComponents.map(comp => {
            return {
                id: comp.id,
                name: comp.name,
                calcof: comp.calcof || "CTC",
                percentage: parseFloat(comp.percentage),
                annual_amount: 0,
                monthly_amount: 0
            }
        });

        // Calculate amounts for deduction components
        const updatedDeductionComponents = deductionComponents.map(comp => {
            return {
                id: comp.id,
                name: comp.name,
                calcof: comp.calcof || "CTC",
                is_amount: comp.is_amount || false,
                annual_amount: 0,
                monthly_amount: 0,
                is_employee_share: comp.is_employee_share || false,
                is_employer_share: comp.is_employer_share || false,
                employee_share_percentage: parseFloat(comp.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(comp.employer_share_percentage) || 0,
                employee_share_monthly_amount: comp.is_amount === true ? Number(comp.monthly_amount) : 0,
                employer_share_monthly_amount: 0
            }
        });

        // Calculate totals
        const totalEarningPercentage = updatedEarningComponents?.reduce((sum, comp) => comp.calcof === "CTC" ? sum + Number(comp.percentage || 0) : sum, 0);
        const totalEarningMonthly = updatedEarningComponents.reduce((sum, comp) => sum + comp.monthly_amount, 0);

        const totalDeductionPercentage = updatedDeductionComponents?.reduce((sum, comp) => sum + Number(comp.employee_share_percentage || 0), 0);
        const totalDeductionMonthly = updatedDeductionComponents.reduce((sum, comp) => sum + comp.monthly_amount, 0);
        const totalEmployeeDeduction = updatedDeductionComponents.reduce((sum, comp) => sum + comp.employee_share_monthly_amount, 0);
        const totalEmployerDeduction = updatedDeductionComponents.reduce((sum, comp) => sum + comp.employer_share_monthly_amount, 0);

        return {
            id: formData.id,
            code: formData.code,
            is_active: formData.is_active,
            salary_template_name: formData.salary_template_name,
            monthly_ctc: 0,
            annual_ctc: 0,
            earning_components: updatedEarningComponents,
            total_earning: {
                annual_amount: 0,
                monthly_amount: 0,
                percentage: totalEarningPercentage
            },
            deduction_components: updatedDeductionComponents,
            total_deduction: {
                annual_amount: totalDeductionMonthly * 12,
                employee_monthly_amount: 0,
                employer_monthly_amount: 0,
                percentage: totalDeductionPercentage
            },
            net_salary: 0,
            department: selectedDept ? selectedDept.id : [],
            designation: selectedDesig ? selectedDesig.id : [],
            is_draft: isdraft
        };
    };

    // Calculate all salary components when monthly CTC changes
    const customConfigLocal = JSON.parse(sessionStorage.getItem("customConfig"));
    const pfLimit = customConfigLocal?.pf_limit || 15000;   // PF basic limit
    const capAmount = customConfigLocal?.cap_amount || 0;   // PF capped monthly amount (e.g., 1800)
    const ESCILimitUpto = customConfigLocal?.esic_limit_upto || 21000; // ESIC limit

    const calculateSalaryBreakdown = (ctc) => {
        // Step 1: Calculate earnings first
        const earnings = earningComponents.map(comp => {
            const monthly = (ctc * (comp.percentage || 0)) / 100;
            return {
                ...comp,
                monthly_amount: monthly,
                annual_amount: monthly * 12
            };
        });

        // Step 2: Extract base components like Basic, HRA etc., for deduction calcof
        const earningsMap = earnings.reduce((acc, curr) => {
            acc[curr.name.toLowerCase()] = curr;
            return acc;
        }, {});

        // Step 3: Calculate deductions with PF & ESIC validation
        const deductions = deductionComponents.map(comp => {
            const baseKey = (comp.calcof || '').toLowerCase(); // e.g., "basic"
            let baseAmount = 0;

            if (baseKey === 'ctc') {
                baseAmount = ctc;
            } else {
                const baseComponent = earningsMap[baseKey];
                baseAmount = baseComponent?.monthly_amount || 0;
            }

            let employeeShare = 0;
            let employerShare = 0;
            let monthlyTotal = 0;

            // Case 1: Manual amount
            if (!comp.is_employee_share && !comp.is_employer_share) {
                monthlyTotal = parseFloat(comp.monthly_amount) || 0;
            } else {
                // Apply PF rules
                if (comp.name.toLowerCase() === "pf") {
                    let pfBase = 0;

                    // ✅ Always include Basic + DA
                    const basicComp = earningsMap["basic"];
                    const daComp = earningsMap["da"];

                    const basicAmount = basicComp ? Number(basicComp.monthly_amount || 0) : 0;
                    const daAmount = daComp ? Number(daComp.monthly_amount || 0) : 0;

                    pfBase = basicAmount + daAmount;

                    // Apply PF cap
                    pfBase = Math.min(pfBase, pfLimit);

                    employeeShare = comp.is_employee_share
                        ? (pfBase * (parseFloat(comp.employee_share_percentage) || 0)) / 100
                        : 0;

                    employerShare = comp.is_employer_share
                        ? (pfBase * (parseFloat(comp.employer_share_percentage) || 0)) / 100
                        : 0;

                    // Cap override
                    if (capAmount > 0 && pfBase === pfLimit) {
                        employeeShare = comp.is_employee_share ? capAmount : 0;
                        employerShare = comp.is_employer_share ? capAmount : 0;
                    }
                }
                else if (comp.name.toLowerCase() === "esic") {
                    if (ctc <= ESCILimitUpto) {
                        employeeShare = comp.is_employee_share ? (baseAmount * (parseFloat(comp.employee_share_percentage) || 0)) / 100 : 0;
                        employerShare = comp.is_employer_share ? (baseAmount * (parseFloat(comp.employer_share_percentage) || 0)) / 100 : 0;
                    } else {
                        employeeShare = 0;
                        employerShare = 0;
                    }

                    // Default deductions
                } else {
                    if (comp.is_employee_share) {
                        employeeShare = (baseAmount * (parseFloat(comp.employee_share_percentage) || 0)) / 100;
                    }
                    if (comp.is_employer_share) {
                        employerShare = (baseAmount * (parseFloat(comp.employer_share_percentage) || 0)) / 100;
                    }
                }

                monthlyTotal = employeeShare + employerShare;
            }

            return {
                ...comp,
                monthly_amount: monthlyTotal,
                annual_amount: monthlyTotal * 12,
                employee_share_monthly_amount: employeeShare,
                employer_share_monthly_amount: employerShare
            };
        });

        // Step 4: Totals
        const totalEarnings = earnings.reduce((sum, comp) => {
            if (comp.calcof === 'CTC') {
                return sum + Number(comp.monthly_amount || 0);
            }
            return sum;
        }, 0);
        const totalDeductions = deductions.reduce((sum, comp) => sum + comp.monthly_amount, 0);

        const totalEmployeeDeductions = deductions.reduce((sum, comp) => {
            const isFixedAmount =
                comp.is_employee_share === false &&
                comp.is_employer_share === false &&
                comp.is_amount === true;

            const employeeAmount = Number(comp.employee_share_monthly_amount || 0);
            const monthly = Number(comp.monthly_amount || 0);

            return sum + (isFixedAmount ? (employeeAmount + monthly) : employeeAmount);
        }, 0);

        const totalEmployerDeductions = deductions.reduce((sum, comp) => sum + comp.employer_share_monthly_amount, 0);

        const basicPayComp = earningsMap["basic"];
        const basicPay = basicPayComp ? basicPayComp.monthly_amount : 0;

        return {
            earnings,
            deductions,
            basicPay,
            totalEarnings,
            totalDeductions,
            totalEmployeeDeductions,
            totalEmployerDeductions,
            netPayMonthly: ctc - (totalEmployeeDeductions),
            netPayAnnual: (ctc - (totalEmployeeDeductions)) * 12
        };
    };

    // Handle monthly CTC change
    const handleMonthlyGrossChange = (value) => {
        const ctc = parseFloat(value) || 0;
        setMonthlyGross(ctc);
        setAnnualGross(ctc * 12);
        setFormData(prev => ({ ...prev, monthly_ctc: ctc, annual_ctc: ctc * 12 }));

        // Calculate all breakdowns
        const breakdown = calculateSalaryBreakdown(ctc);

        // Update state with calculated values
        setBasicPay(breakdown.basicPay);
        setTotalEarnings(breakdown.totalEarnings);
        setTotalDeductions(breakdown.totalDeductions);
        setNetPayMonthly(breakdown.netPayMonthly);
        setNetPayAnnual(breakdown.netPayAnnual);

        // Update components with calculated amounts
        setEarningComponents(breakdown.earnings);
        setDeductionComponents(breakdown.deductions);
    };

    // submit handler
    const handleSubmit = async (isdraft = false) => {
        try {
            setLoadings(true);

            // Prepare the payload
            const payload = preparePayload(
                formData,
                earningComponents,
                deductionComponents,
                selectedDept,
                selectedDesig,
                isdraft
            );

            // console.log("Submitting payload:", payload);

            // Use the appropriate method from useApiMethods
            if (fromEdit) {
                // For PUT/update
                const response = await update(SALARY_TEMPLATE, payload, false, setLoadings);
                handleApiResponse(response);
            } else {
                // For POST/create
                const response = await create(SALARY_TEMPLATE, payload, false, setLoadings);
                handleApiResponse(response);
            }

        } catch (error) {
            console.error("Submit error:", error);
            ToastMsg("error", error.message || "Failed to create salary template");
            setLoadings(false);
        }
    };

    // Helper function to handle API response
    const handleApiResponse = (data) => {
        if (data?.status === "success") {
            ToastMsg("success", data.message);
            setFormData(initialFormData);
            navigate('/configuration/salarytemplate');
        } else if (data?.status === "fail") {
            ToastMsg("error", data.message);
        } else {
            ToastMsg("error", "Unexpected response from server");
        }
        setLoadings(false);
    };

    const handleInitalCancelBtn = () => {
        if (formData.is_draft) {
            navigate('/configuration/salarytemplate');
        } else {
            if (!formData.salary_template_name && !formData.salary_designation && !formData.salary_department) {
                navigate('/configuration/salarytemplate');
            } else {
                setIsCancelClk(true);
            }
        }

    };

    const handleCancelBtn = () => {
        setIsCancelClk(false); // Just close the modal & navigate
        navigate('/configuration/salarytemplate');
    };

    const handleSaveAsDraft = () => {
        // console.log("Saving as draft...");
        // send the draft value as true
        handleSubmit(true)
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // render functions

    // conditional rendering

    // return JSX

    if (loadings) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard className="dark:bg-gray-900">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    onClick={handleCancelBtn}
                />
                <div className="flex-1">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Salary Template" : "Add Salary Template"}
                    </h1>
                    <p className="text-sm text-muted-foreground dark:text-gray-400 mt-1">
                        Define and manage salary structures and compensation templates
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                !fromEdit && !isActive ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={!fromEdit && !isActive}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 shadow-sm",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* template details */}
            <div className="grid grid-cols-[2fr_1fr] gap-4 mt-2">
                {/* Column 1: Three cards stacked vertically */}
                <div className="flex flex-col gap-4 overflow-y-auto h-[calc(100vh-180px)]">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-sidebar-primary dark:bg-blue-500 rounded-full"></div>
                            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Template Details</h3>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            Set up the basic information for this salary template.
                        </span>

                        {/* Form Inputs */}
                        <div className="mt-4 grid grid-cols-1 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Template Name</label>
                                <input
                                    type="text"
                                    placeholder="Enter template name"
                                    value={formData.salary_template_name}
                                    onChange={(e) => setFormData({ ...formData, salary_template_name: e.target.value })}
                                    required
                                    disabled={loadings}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors duration-200"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Department */}
                                <CustomDropdown
                                    label="Department"
                                    list={dropdownList?.departmentlist || []}
                                    displayKey="department_name"
                                    selectedItem={selectedDept}
                                    setSelectedItem={setSelectedDept}
                                />

                                {/* Designation */}
                                <CustomDropdown
                                    label="Designation"
                                    list={dropdownList?.designationList || []}
                                    displayKey="designation_name"
                                    selectedItem={selectedDesig}
                                    setSelectedItem={setSelectedDesig}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* earnings */}
                        <ComponentTable
                            type="Earnings"
                            components={earningComponents}
                            setComponents={setEarningComponents}
                            monthlyGross={formData.monthly_ctc}
                            componentList={dropdownList.earningComponents}
                            earningComponents={earningComponents}
                        />
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        {/* deductions */}
                        <ComponentTable
                            type="Deductions"
                            components={deductionComponents}
                            setComponents={setDeductionComponents}
                            monthlyGross={formData.monthly_ctc}
                            componentList={dropdownList.deductionComponents}
                            earningComponents={earningComponents}
                        />
                    </div>
                </div>

                {/* Column 2: One card spanning the full height */}
                <div className="sticky top-4 self-start">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-[calc(100vh-240px)] overflow-y-auto mb-2">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-sidebar-primary dark:bg-blue-500 rounded-full"></div>
                            <h3 className="text-md font-semibold text-gray-900 dark:text-white">Salary Preview</h3>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 mb-4 block">Real-time salary breakdown preview</span>

                        {/* Monthly Gross Input */}
                        <div className="mb-3">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Monthly Gross</label>
                            <input
                                type="number"
                                value={monthlyGross}
                                onChange={(e) => handleMonthlyGrossChange(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors duration-200"
                                placeholder="Enter Monthly Gross"
                            />
                        </div>

                        <div className='flex justify-between items-center mb-3 text-gray-600 dark:text-gray-400'>
                            <span className='flex gap-2 items-center'>
                                <label className="text-xs font-medium">Monthly CTC</label>
                                <Tooltip title={
                                    <span className='text-gray-100 text-xs'>
                                        CTC = Gross + Employer Contributions (PF + ESIC + Others)
                                    </span>
                                }>
                                    <TriangleAlert className="text-gray-400 dark:text-gray-500 h-3 w-3 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200" />
                                </Tooltip>
                            </span>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                                ₹{(monthlyGross + totalEmployerDeductions) || 0}
                            </span>
                        </div>

                        {/* Annual Gross Input */}
                        <div className="mb-3">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Annual Gross</label>
                            <input
                                type="number"
                                value={annualGross}
                                readOnly
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-xs bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white border-dashed cursor-not-allowed"
                                placeholder="Annual Gross"
                            />
                        </div>

                        <div className='flex justify-between items-center mb-4 text-gray-600 dark:text-gray-400'>
                            <span className='flex gap-2 items-center'>
                                <label className="text-xs font-medium">Annual CTC</label>
                                <Tooltip title={
                                    <span className='text-gray-100 text-xs'>
                                        CTC = Gross + Employer Contributions (PF + ESIC + Others)
                                    </span>
                                }>
                                    <TriangleAlert className="text-gray-400 dark:text-gray-500 h-3 w-3 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200" />
                                </Tooltip>
                            </span>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                                ₹{((monthlyGross + totalEmployerDeductions) * 12) || 0}
                            </span>
                        </div>

                        <hr className="my-4 border-gray-200 dark:border-gray-600" />

                        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">Salary Summary</h4>

                        <div className="text-xs space-y-3 border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Basic Pay:</span>
                                <span className="font-medium text-gray-900 dark:text-white">₹{basicPay?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Total Earnings:</span>
                                <span className="font-medium text-green-600 dark:text-green-400">₹{totalEarnings?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                                <span className="font-medium text-red-600 dark:text-red-400">₹{totalDeductions?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
                                <span className="font-semibold text-gray-900 dark:text-white">Net Pay (Monthly):</span>
                                <span className="font-bold text-gray-900 dark:text-white">₹{netPayMonthly?.toFixed(2) || '0.00'}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-900 dark:text-white">Net Pay (Annual):</span>
                                <span className="font-bold text-gray-900 dark:text-white">₹{netPayAnnual?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex bg-white dark:bg-gray-800 rounded-lg justify-end gap-3 p-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <Button
                            variant="outline"
                            onClick={handleCancelBtn}
                            disabled={loadings}
                            className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </Button>

                        {/* Draft Button - Only show in create mode */}
                        {!fromEdit && (
                            <Button
                                onClick={handleSaveAsDraft}
                                disabled={loadings || !isFormValid}
                                className={cn(
                                    "bg-gray-600 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-700",
                                    "text-white transition-colors duration-200",
                                    "flex items-center gap-2",
                                    (loadings || !isFormValid) && "opacity-50 cursor-not-allowed"
                                )}
                            >
                                Save as Draft
                            </Button>
                        )}

                        <Button
                            onClick={() => handleSubmit(false)}
                            disabled={loadings || (!isFormValid && !fromEdit)}
                            className={cn(
                                "bg-sidebar-primary hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (loadings || (!isFormValid && !fromEdit)) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            {loadings ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {loadings ? "Saving..." : (fromEdit ? "Update" : "Save Policy")}
                        </Button>
                    </div>
                </div>
            </div>
        </ContainerCard>
    );
}

export default SalaryTemplateAddEdit;